import { useEffect, useState, type CSSProperties, type RefObject } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import type * as DialogPrimitive from '@radix-ui/react-dialog';

const ANCHORED_OVERLAY_GAP_PX = 4;

/** Marks portalled combobox listboxes so sheet/dialog layers allow pointer events through. */
export const ANCHORED_LISTBOX_ATTR = 'data-anchored-listbox';

const NESTED_RADIX_OVERLAY_ATTRS = [
  'data-radix-select-viewport',
  'data-radix-popper-content-wrapper',
  'data-radix-select-content',
  'data-radix-menu-content',
  'data-radix-popover-content',
  'data-radix-dropdown-menu-content',
] as const;

type OverlayPointerTargetOptions = {
  anchorRef?: RefObject<HTMLElement | null>;
  overlayRef?: RefObject<HTMLElement | null>;
};

type DismissableLayerEvent = Event & {
  detail?: { originalEvent?: Event };
};

/**
 * Radix DismissableLayer wraps native pointer events; unwrap before composedPath.
 */
function getPointerEventPath(event: Event): EventTarget[] {
  const maybeLayerEvent = event as DismissableLayerEvent;
  const originalEvent = maybeLayerEvent.detail?.originalEvent;
  if (originalEvent instanceof Event && typeof originalEvent.composedPath === 'function') {
    return originalEvent.composedPath();
  }
  if (typeof event.composedPath === 'function') {
    return event.composedPath();
  }
  return [];
}

/**
 * True when a pointer event targets a portalled combobox listbox or nested Radix
 * menu (Select, Popover). Uses composedPath so it works across portal boundaries.
 */
export function isPortalledOverlayPointerTarget(
  event: Event,
  options?: OverlayPointerTargetOptions,
): boolean {
  const path = getPointerEventPath(event);
  if (options?.anchorRef?.current && path.includes(options.anchorRef.current)) {
    return true;
  }
  if (options?.overlayRef?.current && path.includes(options.overlayRef.current)) {
    return true;
  }

  for (const node of path) {
    if (!(node instanceof HTMLElement)) continue;
    if (node.hasAttribute(ANCHORED_LISTBOX_ATTR)) return true;
    // Google Places legacy `.pac-container` (appended outside Radix portals).
    if (node.closest('.pac-container')) {
      return true;
    }
    for (const attr of NESTED_RADIX_OVERLAY_ATTRS) {
      if (node.hasAttribute(attr)) return true;
    }
  }

  const maybeLayerEvent = event as DismissableLayerEvent;
  const directTarget = maybeLayerEvent.detail?.originalEvent?.target ?? event.target;
  if (directTarget instanceof Element && directTarget.closest('.pac-container')) {
    return true;
  }

  return false;
}

type DialogContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;

/** Prevents Radix Sheet/Dialog from swallowing clicks on portalled combobox layers. */
function preventDismissWhenPortalledOverlay(event: Event): void {
  if (isPortalledOverlayPointerTarget(event)) {
    event.preventDefault();
  }
}

export const portalledOverlaySheetDismissProps: Pick<
  DialogContentProps,
  'onInteractOutside' | 'onPointerDownOutside' | 'onFocusOutside'
> = {
  onInteractOutside: preventDismissWhenPortalledOverlay,
  onPointerDownOutside: preventDismissWhenPortalledOverlay,
  onFocusOutside: preventDismissWhenPortalledOverlay,
};

export type AnchoredOverlayPlacement = 'above' | 'below' | 'auto';

type AnchoredOverlayPositionOptions = {
  /** Prefer opening above/below, or flip based on available viewport space. */
  placement?: AnchoredOverlayPlacement;
  /** Cap list height (px); also used when deciding whether to flip. */
  maxHeightPx?: number;
};

const DEFAULT_OVERLAY_MAX_HEIGHT_PX = 240;

/**
 * Tracks a fixed-position overlay relative to its anchor through scroll and
 * resize. Flips above the trigger when there isn't enough room below
 * (`placement: 'auto'`). Used when the anchor sits inside clipped containers.
 */
export function useAnchoredOverlayPosition(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  options?: AnchoredOverlayPositionOptions,
): CSSProperties | null {
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const placement = options?.placement ?? 'below';
  const maxHeightPx = options?.maxHeightPx ?? DEFAULT_OVERLAY_MAX_HEIGHT_PX;

  useEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }

    function update() {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - ANCHORED_OVERLAY_GAP_PX;
      const spaceAbove = rect.top - ANCHORED_OVERLAY_GAP_PX;
      const placeAbove =
        placement === 'above' ||
        (placement === 'auto' &&
          spaceBelow < Math.min(maxHeightPx, 160) &&
          spaceAbove > spaceBelow);
      const available = placeAbove ? spaceAbove : spaceBelow;
      const maxHeight = Math.min(maxHeightPx, Math.max(120, available));

      if (placeAbove) {
        setStyle({
          position: 'fixed',
          bottom: window.innerHeight - rect.top + ANCHORED_OVERLAY_GAP_PX,
          left: rect.left,
          width: rect.width,
          maxHeight,
        });
        return;
      }

      setStyle({
        position: 'fixed',
        top: rect.bottom + ANCHORED_OVERLAY_GAP_PX,
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
    }

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRef, maxHeightPx, open, placement]);

  return style;
}

/**
 * Closes combobox overlays when the user clicks outside the anchor and listbox.
 * Ignores Radix portaled layers (Select, Popover, etc.) nested inside the overlay.
 */
export function useDismissOnOutsidePointerDown(
  open: boolean,
  onDismiss: () => void,
  anchorRef: RefObject<HTMLElement | null>,
  overlayRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (isPortalledOverlayPointerTarget(event, { anchorRef, overlayRef })) return;
      onDismiss();
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [anchorRef, onDismiss, open, overlayRef]);
}
